import { useEffect, useState } from 'react';
import { getLesson, nextLesson } from '../content/lessons.js';
import { PRACTICE_TEXTS, textToLines } from '../content/texts.js';
import { defaultModeFor } from '../content/reference-modes.js';
import { resolveLayout } from '../engine/layouts.js';
import { store, useStoreRevision } from './store';
import { navigate, useRoute, type Route } from './router';
import { Landing } from './screens/Landing';
import { Lessons, TextsView } from './screens/Lessons';
import { PracticeView } from './screens/Practice';
import { ProgressView } from './screens/ProgressView';
import { SettingsView } from './screens/SettingsView';
import { CalibrateView, GuideView } from './screens/GuideView';

export function App() {
  const route = useRoute();
  const [customText, setCustomText] = useState('');
  useStoreRevision();

  const settings = store.getSettings();
  const layout = resolveLayout(settings.layoutId, store.calibration(settings.layoutId));

  useEffect(() => {
    document.body.className = `font-${settings.fontSize}`;
  }, [settings.fontSize]);

  const isActive = (name: Route['name']) => (route.name === name ? 'page' : undefined);

  return (
    <div className="app">
      <a className="skip-link" href="#main">Preskočiť na obsah</a>

      <header className="app-header">
        <a className="brand" href="#/">
          <span className="brand-mark" aria-hidden="true">10</span>
          <span className="brand-text">
            <span className="app-title">Písanie všetkými desiatimi</span>
            <span className="app-subtitle">
              {layout.name} · bez reklám, bez cookies, funguje offline
            </span>
          </span>
        </a>
        <a className="btn btn-primary" href="#/lekcie">
          {store.completedLessonIds().length > 0 ? 'Pokračovať v lekciách' : 'Začať'}
        </a>
      </header>

      <nav className="nav" aria-label="Hlavná navigácia">
        <a href="#/" aria-current={isActive('home')}>Úvod</a>
        <a href="#/lekcie" aria-current={isActive('lessons')}>Lekcie</a>
        <a href="#/texty" aria-current={isActive('texts')}>Cvičné texty</a>
        <a href="#/pokrok" aria-current={isActive('progress')}>Pokrok</a>
        <a href="#/sprievodca" aria-current={isActive('guide')}>Sprievodca</a>
        <a href="#/kalibracia" aria-current={isActive('calibrate')}>Kalibrácia</a>
        <a href="#/nastavenia" aria-current={isActive('settings')}>Nastavenia</a>
      </nav>

      <main id="main">
        {route.name === 'home' && <Landing />}
        {route.name === 'lessons' && <Lessons />}
        {route.name === 'texts' && (
          <TextsView
            onStartCustom={(text) => {
              setCustomText(text);
              navigate('#/vlastny');
            }}
          />
        )}
        {route.name === 'lesson' && <LessonRoute lessonId={route.lessonId} mode={route.mode} />}
        {route.name === 'text' && <TextRoute textId={route.textId} />}
        {route.name === 'custom' &&
          (customText.trim() ? (
            <PracticeView id="custom" title="Vlastný text" subtitle="Text, ktorý ste vložili" text={customText} />
          ) : (
            <div className="card">
              <h2>Vlastný text</h2>
              <p className="muted">
                Najprv vložte text na obrazovke <a href="#/texty">Cvičné texty</a>.
              </p>
            </div>
          ))}
        {route.name === 'progress' && <ProgressView />}
        {route.name === 'settings' && <SettingsView />}
        {route.name === 'guide' && <GuideView />}
        {route.name === 'calibrate' && <CalibrateView />}
      </main>
    </div>
  );
}

function LessonRoute({ lessonId, mode }: { lessonId: string; mode?: string }) {
  const lesson = getLesson(lessonId);
  if (!lesson) {
    return (
      <div className="card">
        <h2>Lekcia sa nenašla</h2>
        <p className="muted">
          Lekcia „{lessonId}“ neexistuje. <a href="#/lekcie">Späť na zoznam lekcií</a>.
        </p>
      </div>
    );
  }

  const following = nextLesson(lesson.id);
  // Režim z adresy musí byť pre túto lekciu dostupný — inak sa použije predvolený.
  const activeMode = mode && lesson.modes.includes(mode) ? mode : defaultModeFor(lesson.number);
  const items = lesson.exercises[activeMode] ?? lesson.items;

  return (
    <PracticeView
      id={`${lesson.id}:${activeMode}`}
      title={lesson.title}
      subtitle={`Lekcia ${lesson.number} · ${lesson.blockTitle}`}
      text={items.join('\n')}
      targets={lesson.targets}
      newKeys={lesson.newKeys}
      lessonNumber={lesson.number}
      lessonId={lesson.id}
      modes={lesson.modes}
      activeMode={activeMode}
      nextHref={following ? `#/cvicenie/${following.id}` : null}
      nextLabel={following ? `Ďalšia lekcia: ${following.title}` : null}
    />
  );
}

function TextRoute({ textId }: { textId: string }) {
  const text = PRACTICE_TEXTS.find((t) => t.id === textId);
  if (!text) {
    return (
      <div className="card">
        <h2>Text sa nenašiel</h2>
        <p className="muted"><a href="#/texty">Späť na zoznam textov</a>.</p>
      </div>
    );
  }
  return (
    <PracticeView
      id={text.id}
      title={text.title}
      subtitle={`${text.author} · ${text.license} · obtiažnosť ${text.difficulty}`}
      text={textToLines(text).join('\n')}
    />
  );
}
